import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { SearchIcon } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import {
    Dialog,
    DialogBody,
    DialogDescription,
    DialogTitle,
    ScrollDialogContent,
    ScrollDialogFooter,
    ScrollDialogHeader,
} from '@/components/ui/dialog-scroll';
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import useDialog from '@/hooks/useDialog';
import server from '@/services/server';
import useClubErrors from '../hooks/useClubErrors';
import useClubPortalContainer from '../hooks/useClubPortalContainer';
import ClubImageUpload from './ClubImageUpload';
import ClubSettingsCardHeader from './ClubSettingsCardHeader';

const seoSchema = z.object({
    seoTitle: z.string().max(70, 'Máximo de 70 caracteres').optional().or(z.literal('')),
    seoDescription: z.string().max(160, 'Máximo de 160 caracteres').optional().or(z.literal('')),
    googleTagManagerId: z.string().max(32, 'ID inválido').optional().or(z.literal('')),
    favicon: z.string().nullable().optional(),
    ogImage: z.string().nullable().optional(),
});

function DialogSeoClub({ data, mutate, open, onOpenChange }) {
    const { setErrors, enqueueSnackbar } = useClubErrors();
    const portalContainer = useClubPortalContainer();

    const form = useForm({
        resolver: zodResolver(seoSchema),
        defaultValues: {
            seoTitle: '',
            seoDescription: '',
            googleTagManagerId: '',
            favicon: '',
            ogImage: '',
        },
    });

    useEffect(() => {
        if (!open || !data) return;

        form.reset({
            seoTitle: data.seoTitle || '',
            seoDescription: data.seoDescription || '',
            googleTagManagerId: data.googleTagManagerId || '',
            favicon: data.favicon || '',
            ogImage: data.ogImage || '',
        });
    }, [open, data, form]);

    const onSubmit = async values => {
        try {
            await server.put('club', {
                seoTitle: values.seoTitle?.trim() || null,
                seoDescription: values.seoDescription?.trim() || null,
                googleTagManagerId: values.googleTagManagerId?.trim() || null,
                favicon: values.favicon || null,
                ogImage: values.ogImage || null,
            });
            await mutate();
            enqueueSnackbar('Configurações de SEO salvas');
            onOpenChange(false);
        } catch (error) {
            setErrors(error);
        }
    };

    const { errors, isSubmitting } = form.formState;
    const faviconPreview = form.watch('favicon');
    const ogImagePreview = form.watch('ogImage');
    const seoTitle = form.watch('seoTitle') || '';
    const seoDescription = form.watch('seoDescription') || '';

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <ScrollDialogContent container={portalContainer} className="sm:max-w-lg">
                <ScrollDialogHeader>
                    <DialogTitle>SEO do site</DialogTitle>
                    <DialogDescription>
                        Título, descrição e imagens usados no Google e ao compartilhar o site do clube.
                    </DialogDescription>
                </ScrollDialogHeader>

                <form onSubmit={form.handleSubmit(onSubmit)} className="contents">
                    <DialogBody>
                        <FieldGroup>
                            <Alert>
                                <AlertDescription>
                                    Se deixar em branco, o site usa o nome e a descrição do clube como padrão.
                                </AlertDescription>
                            </Alert>

                            <Field data-invalid={Boolean(errors.seoTitle)}>
                                <FieldLabel htmlFor="club-seo-title">Título (meta title)</FieldLabel>
                                <Input
                                    id="club-seo-title"
                                    placeholder="Ex.: Meu Clube — Clube de Benefícios"
                                    maxLength={70}
                                    {...form.register('seoTitle')}
                                    aria-invalid={Boolean(errors.seoTitle)}
                                />
                                <FieldDescription>{seoTitle.length}/70 caracteres</FieldDescription>
                                <FieldError>{errors.seoTitle?.message}</FieldError>
                            </Field>

                            <Field data-invalid={Boolean(errors.seoDescription)}>
                                <FieldLabel htmlFor="club-seo-description">Descrição (meta description)</FieldLabel>
                                <Textarea
                                    id="club-seo-description"
                                    rows={3}
                                    placeholder="Resumo do clube para buscadores e redes sociais"
                                    maxLength={160}
                                    {...form.register('seoDescription')}
                                    aria-invalid={Boolean(errors.seoDescription)}
                                />
                                <FieldDescription>{seoDescription.length}/160 caracteres</FieldDescription>
                                <FieldError>{errors.seoDescription?.message}</FieldError>
                            </Field>

                            <Field data-invalid={Boolean(errors.googleTagManagerId)}>
                                <FieldLabel htmlFor="club-gtm-id">Google Tag Manager (opcional)</FieldLabel>
                                <Input
                                    id="club-gtm-id"
                                    placeholder="GTM-XXXXXXX"
                                    {...form.register('googleTagManagerId')}
                                    aria-invalid={Boolean(errors.googleTagManagerId)}
                                />
                                <FieldDescription>
                                    ID do contêiner GTM para métricas e tags de marketing no site.
                                </FieldDescription>
                                <FieldError>{errors.googleTagManagerId?.message}</FieldError>
                            </Field>

                            <Field>
                                <FieldLabel>Favicon</FieldLabel>
                                <ClubImageUpload
                                    name="favicon"
                                    value={faviconPreview}
                                    onChange={(_, value) =>
                                        form.setValue('favicon', value ?? '', { shouldDirty: true })
                                    }
                                    canvaWidth={64}
                                    canvaHeight={64}
                                    objectFit="contain"
                                    label="Ícone exibido na aba do navegador. Recomendado: 64x64px, PNG quadrado."
                                />
                            </Field>

                            <Field>
                                <FieldLabel>Imagem de compartilhamento (Open Graph)</FieldLabel>
                                <ClubImageUpload
                                    name="ogImage"
                                    value={ogImagePreview}
                                    onChange={(_, value) =>
                                        form.setValue('ogImage', value ?? '', { shouldDirty: true })
                                    }
                                    canvaWidth={1200}
                                    canvaHeight={630}
                                    fluidPreview
                                    objectFit="cover"
                                    label="Imagem ao compartilhar links nas redes. Recomendado: 1200x630px."
                                />
                            </Field>
                        </FieldGroup>
                    </DialogBody>

                    <ScrollDialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            Cancelar
                        </Button>
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? <Spinner className="size-4" /> : 'Salvar'}
                        </Button>
                    </ScrollDialogFooter>
                </form>
            </ScrollDialogContent>
        </Dialog>
    );
}

export default function SeoClub({ data, mutate }) {
    const { open, handleClose } = useDialog('#seo-clube');
    const hasSeo =
        Boolean(data?.seoTitle?.trim()) ||
        Boolean(data?.seoDescription?.trim()) ||
        Boolean(data?.favicon) ||
        Boolean(data?.ogImage) ||
        Boolean(data?.googleTagManagerId?.trim());

    return (
        <>
            <Card>
                <CardHeader>
                    <ClubSettingsCardHeader
                        icon={SearchIcon}
                        title="SEO do site"
                        description="Título, descrição, favicon e imagem de compartilhamento do site do clube."
                    />
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                    <div>
                        <span className="text-muted-foreground">Título</span>
                        <p className="font-medium">{data?.seoTitle?.trim() || 'Padrão do clube'}</p>
                    </div>
                    <div>
                        <span className="text-muted-foreground">Descrição</span>
                        <p className="line-clamp-2 font-medium">
                            {data?.seoDescription?.trim() || 'Padrão do clube'}
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-4">
                        <div>
                            <span className="text-muted-foreground">Favicon</span>
                            <p className="font-medium">{data?.favicon ? 'Configurado' : 'Usa o logo'}</p>
                        </div>
                        <div>
                            <span className="text-muted-foreground">Imagem OG</span>
                            <p className="font-medium">{data?.ogImage ? 'Configurada' : 'Usa banner padrão'}</p>
                        </div>
                        <div>
                            <span className="text-muted-foreground">GTM</span>
                            <p className="font-medium">{data?.googleTagManagerId?.trim() || 'Não configurado'}</p>
                        </div>
                    </div>
                </CardContent>
                <CardFooter>
                    <Button variant="outline" size="sm" render={<Link to="#seo-clube" />}>
                        <SearchIcon className="size-4" />
                        {hasSeo ? 'Editar' : 'Configurar SEO'}
                    </Button>
                </CardFooter>
            </Card>

            <DialogSeoClub data={data} mutate={mutate} open={open} onOpenChange={isOpen => !isOpen && handleClose()} />
        </>
    );
}
